import { useState, useRef, useCallback, useEffect } from 'react';

export default function useColumnResize({ onColumnWidthChange, controlledWidths } = {}) {
    const [internalWidths, setInternalWidths] = useState({});
    const columnWidths = controlledWidths ?? internalWidths;

    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef(null);

    const updateWidth = useCallback(
        (colKey, width) => {
            const update = (prev) => ({ ...prev, [colKey]: width });
            if (controlledWidths && onColumnWidthChange) {
                onColumnWidthChange(update(controlledWidths));
            } else {
                setInternalWidths(update);
                if (onColumnWidthChange) onColumnWidthChange(update(internalWidths));
            }
        },
        [controlledWidths, onColumnWidthChange, internalWidths]
    );

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            if (!resizeRef.current) return;
            const { colKey, startX, startWidth, minWidth, maxWidth } = resizeRef.current;
            const delta = e.clientX - startX;
            const lo = Number.isFinite(minWidth) ? minWidth : 60;
            const hi = Number.isFinite(maxWidth) ? maxWidth : 600;
            const newWidth = Math.min(hi, Math.max(lo, startWidth + delta));
            updateWidth(colKey, newWidth);
        };

        const handleMouseUp = () => {
            resizeRef.current = null;
            setIsResizing(false);
            document.body.style.cursor = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, updateWidth]);

    const getResizeHandleProps = useCallback(
        (colKey, currentWidth, minWidth = 60, maxWidth = 600) => ({
            onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
                // У колонки без явной width стартовая ширина неизвестна (она
                // пришла из замера контента) — берём фактическую с экрана,
                // иначе арифметика даёт NaN и столбец не двигается.
                const th = e.currentTarget.closest('th');
                const measured = th ? Math.round(th.getBoundingClientRect().width) : null;
                const known = columnWidths[colKey] ?? currentWidth;
                const startWidth = Number.isFinite(known) ? known : (measured || 150);
                resizeRef.current = {
                    colKey,
                    startX: e.clientX,
                    startWidth,
                    minWidth,
                    maxWidth,
                };
                setIsResizing(true);
                document.body.style.cursor = 'col-resize';
            },
        }),
        [columnWidths]
    );

    const resetColumnWidth = useCallback(
        (colKey) => {
            if (controlledWidths && onColumnWidthChange) {
                const next = { ...controlledWidths };
                delete next[colKey];
                onColumnWidthChange(next);
            } else {
                setInternalWidths((prev) => {
                    const next = { ...prev };
                    delete next[colKey];
                    return next;
                });
            }
        },
        [controlledWidths, onColumnWidthChange]
    );

    return { columnWidths, isResizing, getResizeHandleProps, resetColumnWidth };
}
